import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Zoom,
} from "@mui/material";
import PropTypes from "prop-types";

import { KeikkaDataContext } from "src/context/KeikkaDataContext";
import { formattedPhoneString, isEmail } from "src/components/js/statics1";

import MyModal from "src/components/layout/MyModal";
import { BetoniToimittajaIcon } from "src/components/icons/ToimittajaIcons";
import { Fragment, lazy, useContext, useEffect, useState } from "react";
import { CloseIcon } from "src/components/icons/ActionIcons";
import MyDivider from "src/components/layout/MyDivider";
import { AsiakasIcon, TyomaaIcon } from "src/components/icons/AsiakasIcons";
import { useNavData } from "src/context/navContext";
import LazyLoader from "src/components/layout/Progress/LazyLoader";
import { useKeikkaBetoniData } from "src/context/KeikkaBetoniContext";
import KeikkaIcon from "src/components/keikka/keikkaIcon/KeikkaIcon";
import { person_fetch_by_id } from "src/data/person/PersonFunctions";
import { useUserData } from "src/context/userContext";
import { TilausIcon } from "src/components/icons/StepIcons";
import { saveKeikkaContactPersonToDB } from "src/data/keikka/handleKeikkaChange";

const Henkilot = lazy(() => import("src/pages/steps/StepTilaus/Rows/HenkiloRow/Henkilot"));
const EditAsiakas = lazy(() => import("src/components/asiakas/edit/EditAsiakas"));
const EditTyomaaModal = lazy(() => import("src/components/tyomaa/edit/EditTyomaaModal"));

const styles = {
  secondRow: {
    paddingLeft: "10px",
    fontSize: "0.92em",
  },
  icon: {
    fontSize: "2em",
  },
  listItemButton: {
    paddingLeft: 2,
    paddingRight: 2,
  },
  listItem: {
    margin: 0,
    padding: 0,
  },
  flex8: { display: "flex", gap: 8 },
};

export default function Yhteystiedot({ isOpen, onClose }) {
  const {
    keikkaData,
    isLoaded: isKeikkaLoaded,
    doSetKeikkaData: setKeikkaData,
  } = useContext(KeikkaDataContext) || {};
  const { token, socket, user } = useUserData();
  const { navToPumppu, navToBetoni } = useNavData();
  const [isTyomaaOpen, setIsTyomaaOpen] = useState(false);
  const [isAsiakasOpen, setIsAsiakasOpen] = useState(false);
  const [isTilausOpen, setIsTilausOpen] = useState(false);
  const { doSetKeikkaBetoniId } = useKeikkaBetoniData();
  const { reloadKeikkaData } = useContext(KeikkaDataContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [tyomaaContactPerson, setTyomaaContactPerson] = useState(null);
  const [asiakasContactPerson, setAsikasContactPerson] = useState(null);
  const [origContactPersonId, setOrigContactPersonId] = useState(0);

  useEffect(() => {
    if (!isKeikkaLoaded) return;

    const asynk = async () => {
      if (keikkaData.keikkaContactPerson.personId !== keikkaData.tyomaa.tyomaaContactPersonId) {
        const _tyomaaPerson = await person_fetch_by_id(
          keikkaData.tyomaa.tyomaaContactPersonId,
          token
        );
        if (_tyomaaPerson && _tyomaaPerson.personPhone !== keikkaData.keikkaContactPerson.person) {
          setTyomaaContactPerson(_tyomaaPerson);
        }
      }
      if (
        keikkaData.asiakas.asiakasContactPersonId > 0 &&
        keikkaData.keikkaContactPerson.personId !== keikkaData.asiakas.asiakasContactPersonId
      ) {
        const _asiakasPerson = await person_fetch_by_id(
          keikkaData.asiakas.asiakasContactPersonId,
          token
        );
        if (
          _asiakasPerson.personPhoneVerified &&
          _asiakasPerson.personPhone !== keikkaData.keikkaContactPerson.personPhone
        ) {
          setAsikasContactPerson(_asiakasPerson);
        }
      }
      setIsLoaded(true);
    };
    asynk();
  }, [isKeikkaLoaded]);

  const handleOpenEditTyomaaClick = () => {
    setIsTyomaaOpen(true);
  };
  const handleCancelTyomaa = () => {
    setIsTyomaaOpen(false);
  };
  const handleSaveTyomaa = () => {
    setIsTyomaaOpen(false);
  };
  const handleOpenEditAsiakasClick = () => {
    setIsAsiakasOpen(true);
  };
  const handleCancelAsiakas = () => {
    setIsAsiakasOpen(false);
  };
  const handleSaveAsiakas = () => {
    setIsAsiakasOpen(false);
  };
  const handleBetoniClick = () => {
    doSetKeikkaBetoniId(keikkaData.betoni[0].keikkaBetoniId);
    navToBetoni();
  };
  const handleTilausContactClick = () => {
    setOrigContactPersonId(keikkaData.keikkaContactPerson.personId);
    setIsTilausOpen(true);
  };
  const handleTilausChange = async () => {
    if (origContactPersonId !== keikkaData.keikkaContactPerson.personId) {
      setOrigContactPersonId(keikkaData.keikkaContactPerson.personId);
      await saveKeikkaContactPersonToDB(keikkaData, token, user.personId, socket);
      reloadKeikkaData();
    }
    setIsTilausOpen(false);
  };
  const handleTilausClose = () => {
    setIsTilausOpen(false);
  };

  const ContactListItem = ({ onClick, IconComponent, primary, secondary, ariaLabel }) => (
    <ListItem className="harmaaTausta" sx={styles.listItem}>
      <ListItemButton sx={styles.listItemButton} onClick={onClick} className="stepRow">
        <ListItemIcon>
          <IconComponent color="primary" style={styles.icon} />
        </ListItemIcon>
        <ListItemText primary={primary} secondary={secondary} aria-label={ariaLabel} />
      </ListItemButton>
    </ListItem>
  );
  ContactListItem.propTypes = {
    onClick: PropTypes.func,
    IconComponent: PropTypes.elementType,
    primary: PropTypes.element,
    secondary: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    ariaLabel: PropTypes.string,
  };

  const createContactListItem = (onClick, IconComponent, primaryData, secondaryData, ariaLabel) => {
    const primary = (
      <>
        <b>{primaryData.title}</b>
        <div className="spaceBetween" style={{ gap: 20 }}>
          <div>{primaryData.name}</div>
          <div>{formattedPhoneString(primaryData.phone)}</div>
        </div>
        {primaryData.secondRow && (
          <div className="spaceBetween" style={styles.secondRow}>
            {primaryData.secondRow}
          </div>
        )}
      </>
    );

    const secondary = isEmail(secondaryData.email) ? secondaryData.email : "";

    return (
      <Fragment>
        <ContactListItem
          onClick={onClick}
          IconComponent={IconComponent}
          primary={primary}
          secondary={secondary}
          ariaLabel={ariaLabel}
        />
        <MyDivider />
      </Fragment>
    );
  };

  return (
    <>
      <LazyLoader>
        {isLoaded && (
          <>
            <EditTyomaaModal
              tyomaaId={keikkaData.tyomaa.tyomaaId}
              isOpen={isTyomaaOpen}
              onClose={handleCancelTyomaa}
              onSave={handleSaveTyomaa}
              keikkaData={keikkaData}
              editKeikkaTyomaa={true}
            />
            <EditAsiakas
              isOpen={isAsiakasOpen}
              onCancel={handleCancelAsiakas}
              onSave={handleSaveAsiakas}
              asiakasId={keikkaData.asiakas.asiakasId}
              editKeikkaAsiakas={true}
            />
          </>
        )}
        <Henkilot
          keikkaData={keikkaData}
          isOpen={isTilausOpen}
          onClose={handleTilausClose}
          onChange={handleTilausChange} // onSaveKlik
          setKeikkaData={setKeikkaData}
        />
      </LazyLoader>
      <MyModal
        isOpen={isOpen}
        slotProps={{ timeout: 1500 }}
        onClose={onClose}
        title="Yhteystiedot"
        closeButtonText="SULJE"
        closeButtonIcon={<CloseIcon />}>
        <Zoom in={isOpen}>
          <Box
            className="dialog"
            sx={{
              margin: "auto",
              "& .MuiTextField-root": { m: 1 },
            }}>
            <List>
              {createContactListItem(
                handleTilausContactClick,
                TilausIcon,
                {
                  title: "Tilaus",
                  name: keikkaData.keikkaContactPerson.personFullName,
                  phone: keikkaData.keikkaContactPerson.personPhone,
                },
                {
                  email: keikkaData.keikkaContactPerson.personEmail,
                },
                "Tilauksen yhteyshenkilö"
              )}

              {createContactListItem(
                handleOpenEditAsiakasClick,
                AsiakasIcon,
                {
                  title: "Asiakas",
                  name: keikkaData.asiakas.asiakasNimi,
                  phone: asiakasContactPerson?.personPhone,
                  secondRow: asiakasContactPerson?.personFullName,
                },
                {
                  email: asiakasContactPerson?.personEmail,
                },
                "Asiakkaan yhteystiedot"
              )}

              {createContactListItem(
                handleOpenEditTyomaaClick,
                TyomaaIcon,
                {
                  title: "Työmaa",
                  name: keikkaData.tyomaa.tyomaaString,
                  phone: tyomaaContactPerson?.personPhone,
                  secondRow: tyomaaContactPerson?.personFullName,
                },
                {
                  email: tyomaaContactPerson?.personEmail,
                },
                "Tyomaan yhteystiedot"
              )}

              {createContactListItem(
                navToPumppu,
                () => (
                  <KeikkaIcon keikkaData={keikkaData} />
                ),
                {
                  title: "Pumppaus",
                  name: keikkaData.pumppuToimittaja.asiakasNimi,
                  phone: keikkaData.pumppuToimittaja.asiakasContactPersonPhone,
                  secondRow: (
                    <div style={{ width: "100%" }}>
                      {keikkaData.pumppu.pumppariList &&
                        keikkaData.pumppu.pumppariList?.map((pumppari, index) => (
                          <div key={"pumppari" + pumppari.personId} className="spaceBetween">
                            <div style={styles.flex8}>
                              {index > 0 && ", "}
                              <span>{pumppari.personFirstName}</span>
                              <span>{keikkaData.pumppu.vehicle.vehicleRegNo}</span>
                              <span>{keikkaData.pumppu.vehicle.vehiclePuomi}m</span>
                            </div>
                            <div>{pumppari.personPhone}</div>
                          </div>
                        ))}
                    </div>
                  ),
                },
                {
                  email: keikkaData.pumppuToimittaja.asiakasContactPersonEmail,
                },
                "Pumpputoimittajan yhteystiedot"
              )}

              {createContactListItem(
                handleBetoniClick,
                BetoniToimittajaIcon,
                {
                  title: "Betoni",
                  name: keikkaData.betoniToimittaja.asiakasNimi,
                  phone: keikkaData.betoniToimittaja.asiakasContactPersonPhone,
                  secondRow: (
                    <div style={styles.flex8}>
                      <span>{keikkaData.betoniToimittaja.sijainti.sijaintiNimi}</span>
                      <span>
                        {formattedPhoneString(keikkaData.betoniToimittaja.sijainti.sijaintiPhone)}
                      </span>
                    </div>
                  ),
                },
                {
                  email: keikkaData.betoniToimittaja.asiakasContactPersonEmail,
                },
                "Betonitoimittajan yhteystiedot"
              )}
            </List>
          </Box>
        </Zoom>
      </MyModal>
    </>
  );
}

Yhteystiedot.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};
